/*
This file is located in the main branch
Last time the file was updated (UTC): [11/6/2024 4:40:59AM]
Permissions to update outside the main branch: [false]
*/

import {
    getFilterElementsDefault,
    getMaskedPhone,
    sharpDateFormatter,
    useWorkplaceState,
} from '@finbeat/react-toolkit';
import { getSmsLogExcel, getSmsLogs } from 'axios/smsLogsApi';
import { BOWorkplace } from 'base-components/BOWorkplace/BOWorkplace';
import useApi from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import { SmsLogFilter, SmsLogTable } from 'models/api/smsLog';
import { useGeneralSettingsContext } from 'providers/GeneralSettingsProvider';
import { useCallback } from 'react';
import { FormatterProps } from 'react-data-grid';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { List } from 'reactstrap';
import { dateTimeFormatter, phoneNumberFormatter } from 'utils/columnFormatters';
import { getEndOfTheDay, getStartOfTheDay } from 'utils/dataTime';
import './SmsLogs.scss';
import SmsLogsFilter from './SmsLogsFilter';

type SmsLogsFormatterProps = FormatterProps<SmsLogTable, SmsLogTable[]>;

const SmsLogs = () => {
    const { t } = useTranslation();
    const api = useApi();
    const { dateTimeFormat } = useGeneralSettingsContext();
    const queryClient = useQueryClient();
    const { hasRole } = useAuth();

    const codeFormatter = useCallback(
        ({ row: { parameters } }: SmsLogsFormatterProps) => (
            <List className="sms-logs__list">
                {parameters.map((item) => (
                    <li key={item.key}>
                        {item.key}: {item.value}
                    </li>
                ))}
            </List>
        ),
        []
    );

    return (
        <BOWorkplace<SmsLogTable, SmsLogTable, SmsLogFilter>
            testId="sms-logs"
            pageTitle="BackOfficeSmsLogs"
            filterParams={{
                sd: getStartOfTheDay(),
                ed: getEndOfTheDay(),
                phone: '',
            }}
            initColumns={[
                {
                    key: 'id',
                    name: <Trans i18nKey={'BackOfficeId'} />,
                },
                {
                    key: 'dt',
                    name: <Trans i18nKey="BackOfficeCreateDt" />,
                    formatter: dateTimeFormatter('dt'),
                },
                { key: 'message', name: <Trans i18nKey="BackOfficeMessage" /> },
                { key: 'sender', name: <Trans i18nKey="BackOfficeSender" /> },
                {
                    key: 'recepient',
                    name: <Trans i18nKey="BackOfficeRecepient" />,
                    formatter: phoneNumberFormatter('recepient'),
                },
                { key: 'status', name: <Trans i18nKey="BackOfficeStatus" /> },
                {
                    key: 'statusDt',
                    name: <Trans i18nKey="BackOfficeStatusDate" />,
                    formatter: dateTimeFormatter('statusDt'),
                },
                {
                    key: 'sentDt',
                    name: <Trans i18nKey="BackOfficeSentDt" />,
                    formatter: dateTimeFormatter('sentDt'),
                },
                { key: 'templateId', name: <Trans i18nKey="BackOfficeTemplate" /> },
                {
                    key: 'parameters',
                    name: <Trans i18nKey="BackOfficeParameters" />,
                    formatter: codeFormatter,
                },
            ]}
            lineHeight={({ row, type }) =>
                type === 'ROW' && row.parameters.length ? row.parameters.length * 35 : 35
            }
            storagePath="smsLog/columns"
            getFilterElements={getFilterElementsDefault([
                {
                    id: 'sd',
                    required: true,
                    formatter: (val) =>
                        `${t('BackOfficeDtFrom')}: ${sharpDateFormatter(val, dateTimeFormat)}`,
                },
                {
                    id: 'ed',
                    required: true,
                    formatter: (val) =>
                        `${t('BackOfficeTo')}: ${sharpDateFormatter(val, dateTimeFormat)}`,
                },
                {
                    id: 'phone',
                    name: t('BackOfficePhone'),
                    formatter: (val) => getMaskedPhone(val),
                },
            ])}
            filter={<SmsLogsFilter />}
            fetchQueryFn={(filter) => getSmsLogs(api, filter)}
            queryKey={['smsLog']}
            queryClient={queryClient}
            fetchExcelQueryFn={(filter) => getSmsLogExcel(api, filter)}
            showExcelBtn={hasRole('smslogExcel')}
        />
    );
};

const useSmsLogsWorkplaceState = () => useWorkplaceState<SmsLogTable, SmsLogTable>();

export default SmsLogs;
export { useSmsLogsWorkplaceState };
